import {
  AppError,
  CommonErrorsKey,
  type SupportedChainId,
  contractBook,
  monad,
} from "@eulerxyz/core-sdk"
import type { Transport } from "@wagmi/core"
import type { Abi } from "abitype"
import {
  AbiErrorSignatureNotFoundError,
  type Account,
  type Address,
  type Chain,
  type Client,
  ContractFunctionRevertedError,
  Hex,
  type StateOverride,
  decodeErrorResult,
  decodeFunctionResult,
  encodeFunctionData,
  publicActions,
  slice,
  toHex,
} from "viem"
import { parseAccount } from "viem/accounts"
import { BaseAggregator, type PendingResolve } from "../BaseAggregator"
import {
  convertStateOverrideToTenderlyStateOverride,
  generateTenderlySimulatorUrl,
} from "../utils"
import { isValidAbi } from "../utils/abi"
import decodeUnknownErrors from "../utils/decode"
import type {
  BatchParameters,
  BatchResult,
  BatchResultStatus,
  ContractFunctionConfig,
  ContractFunctionResult,
  FailureBatchResult,
  SimulateBatchResults,
  SuccessBatchResult,
} from "./types"
import { type EVCCall, type EVCCalls, isEVCCall, mapToEVCCall } from "./utils"

export type EvcBatchSimulationAggregatorArgs<chain extends Chain | undefined> =
  Partial<Omit<BatchParameters<chain>, "contracts" | "value">>

export type StatusCheck = {
  checkedAddress: Address
} & (
  | {
      status: "success"
    }
  | {
      status: "failure"
      error: ContractFunctionRevertedError
    }
)

interface EvcBatchSimulationAggregatorArgsResults<
  contracts extends ContractFunctionConfig[],
> {
  results: SimulateBatchResults<contracts>
  rawResults: BatchResultStatus<ContractFunctionResult>[]
  accountStatusResults: StatusCheck[]
  vaultStatusResults: StatusCheck[]
}

/**
 * Represents a utility class for making batched contract transactions using the EVC contract.
 */
export default class EvcBatchSimulationAggregator<
  chain extends Chain | undefined,
  transport extends Transport = Transport,
> extends BaseAggregator<
  SuccessBatchResult<any> | FailureBatchResult,
  transport,
  chain
> {
  /**
   * The arguments for the batched contract transactions.
   */
  args: EvcBatchSimulationAggregatorArgs<chain>

  /**
   * An array of pending evc batch contracts to execute.
   * @private
   */
  #pendingCalls: ContractFunctionConfig[] = []

  /**
   * An array of pending raw evc batch calls to execute.
   * @private
   */
  #pendingRawCalls: EVCCalls = []

  /**
   * An array of pending resolve functions for raw evc requests.
   * @protected
   */
  #pendingRawResolves: PendingResolve<BatchResultStatus<any>>[] = []

  /**
   * An array of pending reject functions for raw evc requests.
   * @protected
   */
  #pendingRawRejects: ((error: Error) => void)[] = []

  /**
   * The latest account status results.
   * @private
   */
  #latestAccountStatusResults: StatusCheck[] = []

  /**
   * The latest vault status results.
   * @private
   */
  #latestVaultStatusResults: StatusCheck[] = []

  /**
   * Create a new instance of the EvcBatchAggregator class.
   * @param client - The client to use for making batched contract transactions.
   * @param args - The arguments for the batched contract transactions.
   */
  constructor(
    client: Client<transport, chain>,
    args: EvcBatchSimulationAggregatorArgs<chain> = {},
  ) {
    super(client)
    this.args = args
  }

  /**
   * Set the arguments for the batched contract transactions.
   * @param args - The arguments for the batched contract transactions.
   */
  setArgs(args: EvcBatchSimulationAggregatorArgs<chain>) {
    this.args = args
  }

  /**
   * Set the state override for the batched contract transactions.
   * @param stateOverride - The state override for the batched contract transactions.
   */
  setStateOverride(stateOverride: StateOverride) {
    this.args.stateOverride = stateOverride
  }

  /**
   * Set the account for the batched contract transactions.
   * @param account - The account for the batched contract transactions.
   */
  setAccount(account: Address | Account) {
    this.args.account = parseAccount(account)
  }

  /**
   * Add a contract function to the EVC batch.
   * @param args - The configuration for the contract function call.
   * @returns {Promise<BatchResult<ContractFunctionResult<abi, functionName>>>} - A promise resolving to the batch result.
   */
  add<abi extends Abi = Abi, functionName extends string = string>(
    args: ContractFunctionConfig<abi, functionName> | EVCCall,
  ): Promise<BatchResultStatus<ContractFunctionResult<abi, functionName>>> {
    if (isEVCCall(args)) return this.addRawCall(args)
    return this.#add(args)
  }

  /**
   * Add a raw call to the EVC batch.
   * @param call - EVC call.
   */
  addRawCall(call: EVCCall): Promise<BatchResultStatus<any>> {
    return this.#addRawCall(call)
  }

  /**
   * Execute a batch of contract function calls in a single transaction and return the value.
   * @param contracts - The configuration for the contract function call.
   * @param rawCalls - Raw EVC calls
   * @returns {Promise<BatchSimulation<contracts>>} - A promise resolving to the batch result.
   */
  async batchSimulation<contracts extends ContractFunctionConfig[]>(
    contracts: contracts,
    rawCalls: EVCCalls = [],
  ): Promise<EvcBatchSimulationAggregatorArgsResults<contracts>> {
    return this.#batch(contracts, rawCalls)
  }

  /**
   * Execute a batch of contract function calls in a single transaction and return the value.
   * @param args - The configuration for the contract function call.
   * @returns {Promise<BatchResult<ContractFunctionResult<abi, functionName>>['result']>} - A promise resolving to the batch result.
   */
  async addValue<abi extends Abi = Abi, functionName extends string = string>(
    args: ContractFunctionConfig<abi, functionName>,
  ): Promise<
    BatchResultStatus<ContractFunctionResult<abi, functionName>>["result"]
  > {
    return this.#add(args).then(({ result }) => result)
  }

  /**
   * Retrieve the latest account status results.
   * @returns {StatusCheck[]} - The latest account status results.
   */
  getAccountStatusResults(): StatusCheck[] {
    return this.#latestAccountStatusResults
  }

  /**
   * Retrieve the latest vault status results.
   * @returns {StatusCheck[]} - The latest vault status results.
   */
  getVaultStatusResults(): StatusCheck[] {
    return this.#latestVaultStatusResults
  }

  /**
   * Internal method to add a contract function to the EVC batch.
   * @param args - The configuration for the contract function call.
   * @private
   * @returns {Promise<BatchResult<ContractFunctionResult<abi, functionName>>} - A promise resolving to the batch result.
   */
  async #add<abi extends Abi = Abi, functionName extends string = string>(
    args: ContractFunctionConfig<abi, functionName>,
  ): Promise<BatchResultStatus<ContractFunctionResult<abi, functionName>>> {
    this.scheduleBatch()

    // Add the call details to the pending calls array.
    this.#pendingCalls.push(args)

    // Return a promise that will be resolved or rejected when the batch is executed.
    return new Promise<BatchResult<ContractFunctionResult<abi, functionName>>>(
      (resolve, reject) => {
        this.pendingResolves.push(resolve)
        this.pendingRejects.push(reject)
      },
    )
  }

  /**
   * Internal method to add a raw call to the EVC batch.
   * @param call - The configuration for the contract function call.
   * @private
   * @returns {Promise<BatchResult<ContractFunctionResult>} - A promise resolving to the batch result.
   */
  #addRawCall(
    call: EVCCall,
  ): Promise<BatchResultStatus<ContractFunctionResult>> {
    this.scheduleBatch()

    this.#pendingRawCalls.push(call)

    return new Promise<BatchResultStatus<any>>((resolve, reject) => {
      this.#pendingRawResolves.push(resolve)
      this.#pendingRawRejects.push(reject)
    })
  }

  /**
   * Internal method to execute a batch of contract function calls in a single transaction.
   * @param contracts - The configuration for the contract function call.
   * @param rawCalls - Raw EVC calls
   * @private
   * @returns {Promise<BatchResult<ContractFunctionResult<abi, functionName>>} - A promise resolving to the batch result.
   */
  async #batch<contracts extends ContractFunctionConfig[]>(
    contracts: contracts,
    rawCalls: EVCCalls = [],
  ): Promise<EvcBatchSimulationAggregatorArgsResults<contracts>> {
    const {
      address: address_,
      account: account_,
      dataSuffix,
      stateOverride,
      maxPriorityFeePerGas,
      maxFeePerGas,
      gas,
      blockNumber,
      nonce,
    } = this.args

    const account = account_ ? parseAccount(account_) : this.client.account

    const address =
      address_ ||
      contractBook.evc.address[this.client?.chain?.id as SupportedChainId]

    // Throw an error if the EVC contract address is not configured for the chain.
    if (typeof address === "undefined")
      throw new AppError(
        CommonErrorsKey.EVCClientNotFound,
        "EVC address not configured for chain.",
      )

    const calls = mapToEVCCall(contracts)
    const args = [...rawCalls, ...calls]
    const encodedCall = encodeFunctionData({
      abi: contractBook.evc.abi,
      functionName: "batchSimulation",
      args: [args],
    })

    if (typeof localStorage !== "undefined") {
      const tenderlyDebugSimulateEVCBatch = localStorage.getItem(
        "tenderlySimulatorUrl",
      )
      if (tenderlyDebugSimulateEVCBatch) {
        const tenderlyOverrides =
          convertStateOverrideToTenderlyStateOverride(stateOverride)
        console.group("Debug Information")
        console.group("TENDERLY_DEBUG_CALL")
        console.log(
          generateTenderlySimulatorUrl(
            account?.address || "",
            address,
            encodedCall,
            tenderlyOverrides,
            this.client?.chain?.id || 1,
          ),
        )
        console.groupEnd()

        console.group("CALLDATA")
        console.log(encodedCall)
        console.groupEnd()

        console.groupEnd()
      }
    }

    const value = args.reduce((acc, call) => acc + call.value, 0n)
    const chainId = this.client?.chain?.id
    const isMonad = chainId === monad.id
    const { result } = await this.client
      .extend(publicActions)
      // @ts-ignore - errors about value, but we require value to be sent in based on values from the calls
      .simulateContract({
        address,
        abi: contractBook?.evc?.abi,
        functionName: "batchSimulation",
        args: [args],
        account,
        stateOverride,
        dataSuffix,
        blockNumber,
        nonce,
        value,
        ...(isMonad
          ? {
              gas: undefined,
            }
          : {
              maxPriorityFeePerGas,
              maxFeePerGas,
              gas,
            }),
      })

    const [batchItemResults, accountStatusResults_, vaultStatusResults_] =
      result

    const rawCallResults = batchItemResults.slice(0, rawCalls.length)
    const contractResults = batchItemResults.slice(rawCalls.length)

    const rawResults: BatchResultStatus<ContractFunctionResult>[] =
      rawCallResults.map((batch) => {
        if (batch.success) {
          return { status: "success", result: batch.result }
        }
        return {
          status: "failure",
          error: { errorName: CommonErrorsKey.RawError },
          data: batch.result,
        }
      })

    const batchResults: BatchResultStatus<ContractFunctionResult>[] =
      contractResults.map((batch, i) => {
        const contract = contracts[i]
        if (!contract)
          throw new AppError(
            CommonErrorsKey.ParamRequired,
            "Contract not found for batch simulation.",
          )

        if (batch.success) {
          if (batch.result === "0x") {
            return {
              status: "success",
              result: batch.result,
            }
          }
          const functionResult = decodeFunctionResult({
            abi: contract.abi,
            args: contract.args,
            data: batch.result,
            functionName: contract.functionName,
          })

          return { status: "success", result: functionResult }
        } else {
          if (batch.result === "0x") {
            console.error("[#batch]batch.result", batch.result)
            return {
              status: "failure",
              error: { errorName: CommonErrorsKey.UnknownError },
              data: batch.result,
            }
          }

          // Checking the error on the error list before start decoding
          const errorName = decodeUnknownErrors(batch.result)
          if (errorName !== CommonErrorsKey.UnknownError) {
            console.error("[#batch]errorName", errorName)
            return {
              status: "failure",
              error: { errorName: errorName },
              data: batch.result,
            } as FailureBatchResult
          }
          /// todo make return ContractFunctionExecutionError
          try {
            let errorMessage = ""
            let errorResult = decodeErrorResult({
              abi: contract.abi,
              data: batch.result,
            })
            if (errorResult.errorName === "Swapper_SwapError") {
              // 0x08c379a0 is selector for string error "Error(string)"
              if (
                errorResult.args?.[1] &&
                slice(errorResult.args[1] as Hex, 0, 4) === "0x08c379a0"
              ) {
                // inner error is a string, replace
                errorResult = decodeErrorResult({
                  abi: [],
                  data: errorResult.args[1] as Hex,
                })
                errorMessage = `Swapper error: ${errorResult.args?.[0]}`
              }
            } else {
              errorMessage =
                typeof errorResult.args?.[0] === "string"
                  ? errorResult.args?.[0]
                  : ""
            }
            return {
              status: "failure",
              error: {
                errorName: errorResult.errorName,
                message: errorMessage,
              },
              data: batch.result,
            }
          } catch (error) {
            if (error instanceof AbiErrorSignatureNotFoundError) {
              for (const details of Object.values(contractBook)) {
                try {
                  // If it's not valid ABI
                  if (!isValidAbi(details.abi)) continue
                  const errorResult = decodeErrorResult({
                    abi: details.abi,
                    data: batch.result,
                  })

                  return {
                    status: "failure",
                    error: {
                      errorName: errorResult.errorName,
                      message:
                        typeof errorResult.args?.[0] === "string"
                          ? errorResult.args?.[0]
                          : "",
                    },
                    data: batch.result,
                  }
                } catch {}
              }
              console.error("[#batch]error", error, batch.result)
              return {
                status: "failure",
                error: { errorName: CommonErrorsKey.UnknownError },
                data: batch.result,
              }
            }

            throw error
          }
        }
      })

    const accountStatusResults = accountStatusResults_.map(
      this.#decodeStatusCheck,
    )

    const vaultStatusResults = vaultStatusResults_.map(this.#decodeStatusCheck)

    this.#latestAccountStatusResults = accountStatusResults
    this.#latestVaultStatusResults = vaultStatusResults

    return {
      results: batchResults as SimulateBatchResults<contracts>,
      rawResults,
      accountStatusResults,
      vaultStatusResults,
    }
  }

  /**
   * Internal method to decode the status check result.
   * @param statusCheck - The status check result.
   * @private
   * @returns {StatusCheck} - The decoded status check result.
   */
  #decodeStatusCheck(
    statusCheck: ContractFunctionResult<
      typeof contractBook.evc.abi,
      "batchSimulation"
    >[1][0],
  ): StatusCheck {
    if (statusCheck.isValid) {
      return { checkedAddress: statusCheck.checkedAddress, status: "success" }
    } else {
      const error = new ContractFunctionRevertedError({
        data: statusCheck.result,
        abi:
          toHex("EVC_ControllerViolation") === statusCheck.result
            ? contractBook.evc.abi
            : contractBook.eVault.abi,
        functionName: "batchSimulation",
      })
      return {
        checkedAddress: statusCheck.checkedAddress,
        status: "failure",
        error,
      }
    }
  }

  /**
   * Internal method to execute a batch immediately.
   * @private
   */
  protected override async makeBatchNow() {
    const pendingCalls = this.#pendingCalls
    const pendingResolves = this.pendingResolves
    const pendingRejects = this.pendingRejects

    const pendingRawCalls = this.#pendingRawCalls
    const pendingRawResolves = this.#pendingRawResolves
    const pendingRawRejects = this.#pendingRawRejects

    this.#pendingCalls = []
    this.pendingResolves = []
    this.pendingRejects = []

    this.#pendingRawCalls = []
    this.#pendingRawResolves = []
    this.#pendingRawRejects = []

    if (pendingCalls.length === 0 && pendingRawCalls.length === 0) return
    try {
      const { results, rawResults } = await this.#batch(
        pendingCalls,
        pendingRawCalls,
      )
      for (const [i, result] of results.entries()) {
        pendingResolves[i]?.(result)
      }
      for (const [i, result] of rawResults.entries()) {
        pendingRawResolves[i]?.(result)
      }
    } catch (error) {
      for (const reject of pendingRejects) {
        reject(error as Error)
      }
      for (const reject of pendingRawRejects) {
        reject(error as Error)
      }
    }
  }
}
